import userData from '../user-data';
import type { TireSize, UserData } from '../user-data';
import type { TireSelector } from './tireselector';

type State = {
  width: number | null;
  height: number | null;
  rim: number | null;
};

class Size {
  dom: {
    root: HTMLElement, // This module's root element
    form: HTMLFormElement,
    width: HTMLSelectElement,
    height: HTMLSelectElement,
    rim: HTMLSelectElement,
  };

  parent: TireSelector;
  name = 'Size';

  // Tyre selector filters state
  #state: State = {
    width: null,
    height: null,
    rim: null,
  };

  constructor(element: HTMLElement, parent: TireSelector) {
    this.dom = {
      root: element,
      form: element.querySelector('form[name="sizeSearchForm"]') as HTMLFormElement,
      width: element.querySelector('select[name="width"]') as HTMLSelectElement,
      height: element.querySelector('select[name="height"]') as HTMLSelectElement,
      rim: element.querySelector('select[name="rim"]') as HTMLSelectElement,
    };

    this.parent = parent;
    this.name = `${this.parent.name} ${this.name}`;

    if(Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary data and dom elements found
      console.log(`[${this.name}] init()`);
      this.log('init settings', this);
      this.#initState();
      this.#attachEventListeners();
    } else {
      console.error(`[${this.name}]: missing data or dom elements`, this);
    }
  }

  /**
   * Initialises state from server or userData if available
   */
  #initState() {
    // Default state is empty
    const initialState = this.getState();

    // Check values from select elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if(this.dom[key]) {
        const value = this.getSelectValue(this.dom[key]);
        initialState[key] = value;
      }
    });

    // Check storage for user's selected size
    const userSize = userData.get('selectedSize');
    if(userSize) {
      Object.keys(userSize).forEach(key => {
        if(this.dom[key]) {
          initialState[key] = userSize[key];
        }
      });
    }

    this.setState(initialState);
  }

  /**
   * Returns current state of size filters
   */
  getState() {
    return {...this.#state};
  }

  /**
   * Sets new state and updates form fields accordingly:
   * Sets available options for each field based on other field values.
   * Sets enabled/disabled state of fields.
   * Sets submit button enabled/disabled state.
   */
  setState(newState: State) {
    const oldState = this.getState();
    const stateKeys = Object.keys(oldState);
    const futureState = { ...oldState, ...newState };

    // Fields are sequential, change one field, next fields are updated
    stateKeys.forEach((key, keyIndex) => {
      const selectElement = this.dom[key] as HTMLSelectElement;
      const previousFieldValue = futureState[stateKeys[keyIndex - 1]];
      const enableField = typeof previousFieldValue === 'undefined' || previousFieldValue !== null;

      if(enableField) {
        const fieldValues = {...futureState};
        const nextFieldKeys = stateKeys.slice(keyIndex); // self and next
        nextFieldKeys.forEach((fieldKey) => {
          fieldValues[fieldKey] = null;
        });
        const matchingSizes = this.filterSizes(fieldValues);
        let availableOptions = matchingSizes.map(size => size[key]);
        // Filter out duplicates
        availableOptions = [...new Set(availableOptions)];
        availableOptions.sort((a: any, b: any) => a - b);

        this.updateSelectOptions(selectElement, availableOptions);
        selectElement.value = futureState[key] !== null ? String(futureState[key]) : '';
        selectElement.disabled = false;
      } else {
        this.updateSelectOptions(selectElement, []);
        selectElement.value = '';
        selectElement.disabled = true;
      }

    });

    // Update internal state
    this.#state = futureState;

    this.log('setState()', {oldState, newState: futureState});

    // Enable submit button if all fields have a value
    const allFieldsHaveValue = Object.values(this.#state).every(value => value !== null);
    const submitBtn = this.dom.form.querySelector('button[type="submit"]') as HTMLButtonElement;
    if(submitBtn) {
      submitBtn.disabled = !allFieldsHaveValue;
    }
  }

  updateSelectOptions(element: HTMLSelectElement, options: any[]) {
    // Remove all options except the empty value one
    Array.from(element.options)
      .filter((opt) => opt.value !== '')
      .forEach((opt) => {
        opt.remove();
      });

    // Populate select with available options
    options.forEach((optionValue) => {
      const option = new Option(String(optionValue), String(optionValue), false);
      element.add(option);
    });
  }

  /**
   * Formats select value to number or null
   * All values are strings in select elements, make empty string null and others numeric.
   */
  getSelectValue(select: HTMLSelectElement) {
    return select.value === '' ? null : Number(select.value);
  }

  /**
   * Filter all available sizes based on given values.
   * All values must match.
   */
  filterSizes(values: State): TireSize[] {
    const matchingSizes = this.parent.settings.sizeData.filter(size => {
      return Object.keys(values).every(key => {
        const stateValue = values[key];
        const sizeValue = size[key];

        if(stateValue && sizeValue !== stateValue) {
          return false;
        }

        return true;
      });
    });

    return matchingSizes;
  }

  #attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));
    this.dom.width.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.height.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.rim.addEventListener('change', this.onSelectChange.bind(this));

    // Subscribe to user data changes
    userData.subscribe('selectedSize', this.onUserDataChange.bind(this));
  }

  /**
   * Handles native select element change event
   */
  onSelectChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const selectName = select.name;
    const selectValue = this.getSelectValue(select);

    const newState = this.getState();
    let resetFields = false;
    for (let key in newState) {
      if (selectName === key) {
        newState[selectName] = selectValue;
        resetFields = true;
      } else if (resetFields) {
        newState[key] = null;
      }
    }

    this.setState(newState);
  }

  onFormSubmit(event:Event) {
    event.preventDefault();
    this.log('onFormSubmit()');

    const sizeStr = `${this.#state.width}/${this.#state.height}R${this.#state.rim}`;

    this.parent.sendAnalyticsEvent({
      event: 'tyre_search_submit',
      tyre_search_type: 'Size Search',
      tyre_size: sizeStr,
    });

    // Clear other user data
    userData.store({license: null, vehicle: null, vehicleNA: null, availableSizes: null, selectedProduct: null});

    this.parent.onSizeSelect(sizeStr);
  }

  /**
   * Form reset clears user's data.
   * Customer request that all data should be cleared, not just size related data.
   * Clearing saved tire size causes an UI update of this form
   */
  onFormReset(event:Event) {
    userData.store({license: null, vehicle: null, vehicleNA: null, availableSizes: null, selectedSize: null, selectedProduct: null});
  }

  /**
   * Reacts to selectedSize changes in UserData from this form and anywhere else on site
   */
  onUserDataChange(selectedSize: UserData['selectedSize']) {
    this.log('onUserDataChange()', selectedSize);
    const currentState = this.getState();

    if(selectedSize === null) {
      // Clear state
      for (let key in currentState) {
        currentState[key] = null;
      }

      this.setState(currentState);
    } else {
      // Don't set state if nothing has changed
      if(Object.keys(currentState).every(key => currentState[key] === selectedSize[key])) {
        return;
      }

      this.setState({
        width: selectedSize?.width || null,
        height: selectedSize?.height || null,
        rim: selectedSize?.rim || null,
      });
    }
  }

  log(message: string, ...optionalParams: any[]) {
    // console.log(`[${this.name}] ${message}`, ...optionalParams);
  }
}

export default Size;
